import type { BloquePublico, PlantillaBloque } from '@ahora-patagonia/contracts';

/**
 * A = portada curada · B = tira de tarjetas con foto · C = lista con miniatura · D = cuadrícula de
 * tres por línea.
 *
 * El handoff reservaba la **D** para otra cosa: "Las más leídas", la disposición de C con la posición
 * numerada al frente. Al construirla quedó claro que eso no era una cuarta disposición sino una
 * propiedad ortogonal —la numeración— porque el editor elige la disposición de ese bloque igual que
 * la de una categoría, y el ranking se tiene que poder leer en cualquiera de ellas. Así que quedó
 * `numerada`, que viaja al lado de la plantilla, y la letra libre.
 *
 * La D de hoy es la cuadrícula, y tiene una diferencia importante con B y C: **no entra en la
 * rotación automática**. Solo se dibuja cuando el editor la pide, así que ninguna portada que nadie
 * tocó cambia de aspecto por existir. La razón es que la cuadrícula no pagina —crece hacia abajo, y
 * con 60 notas son 20 filas—, y que eso le pase solo a un bloque porque le tocó el turno sería una
 * portada distinta sin que nadie la haya decidido.
 */
export type Plantilla = 'A' | 'B' | 'C' | 'D';

/** Lo que el editor eligió en el panel, traducido al vocabulario de plantillas del sitio. */
const DEL_CONTRATO: Record<PlantillaBloque, Plantilla> = {
  TIRA: 'B',
  LISTA: 'C',
  CUADRICULA: 'D',
};

export interface Presentacion {
  bloque: BloquePublico;
  plantilla: Plantilla;
  /** Ground alternado: página (`bg`) o franja (`#EAE9E9`). */
  ground: 'bg' | 'franja';
  /**
   * Las notas van numeradas (1, 2, 3…). Solo "Más leídas": es el único bloque donde el orden
   * comunica algo, y numerar donde no lo comunica sería inventar una jerarquía.
   */
  numerada: boolean;
}

/**
 * La regla de rotación de la home (handoff §1.2): la portada es A; los bloques de categoría
 * alternan B → C → B → C; Última hora se dibuja como lista (C) en el cuerpo; y Más leídas entra en
 * la misma alternancia que una categoría, porque se configura como una. Los grounds alternan
 * página/franja por bloque.
 *
 * **La alternancia de plantilla es el default, no una garantía.** Cuando el editor eligió la
 * disposición de un bloque manda lo elegido: la portada puede quedar con cuatro secciones seguidas
 * iguales y es una decisión del cliente, no algo para corregir en silencio. El bloque que no eligió
 * nada —todos, hasta que alguien los toca— sigue alternando, y trata de no repetir la plantilla del
 * bloque anterior aunque ese anterior venga elegido a mano.
 *
 * El **ground** sí sigue alternando siempre por posición: es lo que separa visualmente dos bloques
 * contiguos, no es algo que el editor pidió elegir, y sin él la portada queda plana.
 *
 * La función es pura y determinista por índice: la home itera bloques activos por
 * `position` **sin conocer sus nombres** — con 5, 7 o 9 bloques la regla es la misma.
 */
/**
 * Los bloques en el orden en que se **apilan en el cuerpo**, que no es el orden en que los manda el
 * servidor: Última hora va al final.
 *
 * En el servidor Última hora es el bloque 1, pegado a la portada, y ahí tiene que quedarse: es
 * estructura del sitio y la API rechaza moverla. Pero esa posición está pensada para **escritorio**,
 * donde Última hora no se apila con nada —vive en la columna lateral, arrancando arriba de todo—.
 * En el cuerpo, en cambio, ser el bloque 1 la pone entre la portada y la primera sección: el lector
 * de teléfono tiene que pasar la lista entera del día antes de llegar a Turismo, y la portada queda
 * separada del resto del diario por una tira de titulares sueltos.
 *
 * Al final cumple el papel que le toca abajo de 1200: el repaso del día para el que llegó hasta el
 * pie, con "Ver todo" a mano para seguir.
 *
 * Ordenar acá y no en el servidor es a propósito: la posición guardada es una sola y la comparten
 * los dos cortes. Lo que cambia entre teléfono y escritorio es **cómo se dibuja**, y eso es del
 * sitio. Además deja el bloque oculto de escritorio al final de la lista en vez de en el medio, así
 * que esconderlo por CSS ya no abre un hueco en la alternancia de grounds.
 */
export function ordenDelCuerpo(bloques: BloquePublico[]): BloquePublico[] {
  const ultimaHora = bloques.filter((b) => b.kind === 'ULTIMA_HORA');
  if (ultimaHora.length === 0) return bloques;
  return [...bloques.filter((b) => b.kind !== 'ULTIMA_HORA'), ...ultimaHora];
}

export function asignarPresentacion(bloques: BloquePublico[]): Presentacion[] {
  const out: Presentacion[] = [];
  let anterior: Plantilla | null = null;
  let alternadorCategoria: Plantilla = 'B';

  for (const [i, bloque] of bloques.entries()) {
    let plantilla: Plantilla;
    if (bloque.kind === 'PORTADA') {
      plantilla = 'A';
    } else if (bloque.kind === 'CATEGORIA' || bloque.kind === 'MAS_LEIDAS') {
      // Los dos que el editor configura, con la misma regla: manda lo elegido y, si no eligió,
      // alterna. Más leídas participa del alternador como cualquier otro para que no quede igual
      // que el bloque de al lado — lo que la distingue es la numeración, no la disposición.
      if (bloque.plantilla) {
        plantilla = DEL_CONTRATO[bloque.plantilla];
      } else {
        plantilla = alternadorCategoria;
        if (plantilla === anterior) plantilla = plantilla === 'B' ? 'C' : 'B';
      }
      // El alternador se sincroniza con lo que se usó, elegido o no: así el próximo bloque
      // automático arranca por la otra plantilla en vez de repetir la de al lado.
      //
      // La cuadrícula no lo mueve: no es ni B ni C, así que no hay nada que "no repetir" después de
      // ella. Si consumiera un turno, elegir una cuadrícula en el medio de la portada daría vuelta la
      // alternancia de todos los bloques automáticos que vienen abajo, que es un efecto que nadie
      // pidió al tocar un solo bloque.
      if (plantilla !== 'D') alternadorCategoria = plantilla === 'B' ? 'C' : 'B';
    } else {
      // ULTIMA_HORA: lista en el cuerpo (<1200; en escritorio vive en la columna lateral).
      plantilla = anterior === 'C' ? 'B' : 'C';
    }

    out.push({
      bloque,
      plantilla,
      ground: i % 2 === 0 ? 'bg' : 'franja',
      numerada: bloque.kind === 'MAS_LEIDAS',
    });
    anterior = plantilla;
  }

  return out;
}
